import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Table, Row, Col, Card, CardBody, Input, Badge,
    Pagination, PaginationItem, PaginationLink, FormGroup, Label
} from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const LorryList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [lorries, setLorries] = useState([]);
    const [summary, setSummary] = useState({ total: 0, active: 0, inactive: 0 });
    const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1 });

    // Filter States
    const [filters, setFilters] = useState({
        search: "",
        status: null, // React select kaga null-ah vachuruken
        page: 1,
        limit: 10
    });

    const statusOptions = [
        { value: "", label: "All Vehicles" },
        { value: "1", label: "Active Vehicles" },
        { value: "0", label: "Inactive Vehicles" }
    ];

    const loadLorries = async () => {
        dispatch(loadingTrue());
        try {
            // Params build panrom
            const params = {
                page: filters.page,
                limit: filters.limit,
                search: filters.search,
                status: filters.status ? filters.status.value : ""
            };

            const queryParams = new URLSearchParams(params).toString();
            const res = await Handler({ method: "GET", url: `/lorry/list.php?${queryParams}` });

            if (res?.success) {
                setLorries(res?.data || []);
                setSummary(res?.summary || { total: 0, active: 0, inactive: 0 });
                setPagination({
                    current_page: res.page,
                    total_pages: res.total_pages,
                    total_records: res.total
                });
            } else {
                toast.error(res?.message || "Failed to fetch lorry list");
            }
        } catch (err) {
            toast.error("Server connection error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    const handleStatusToggle = async (id, currentStatus) => {
        const newStatus = currentStatus == 1 ? 0 : 1;
        const result = await Swal.fire({
            title: 'Update Vehicle Status?',
            text: `Change status to ${newStatus === 1 ? 'Active' : 'Inactive'}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#2e4a3d',
            confirmButtonText: 'Yes, Update'
        });

        if (result.isConfirmed) {
            dispatch(loadingTrue());
            try {
                const res = await Handler({
                    method: "POST",
                    url: "/lorry/update.php", // Unnoda update path pathuko machan
                    data: { id, status: newStatus }
                });

                if (res?.success) {
                    toast.success("Status updated successfully");
                    loadLorries();
                }
            } catch (e) { toast.error("Update failed"); }
            finally { dispatch(loadingFalse()); }
        }
    };

    useEffect(() => { loadLorries(); }, [filters.page, filters.status]);

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            borderRadius: '8px',
            height: '45px',
            borderColor: '#dee2e6'
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    return (
        <div className="page-container p-1">
            <div className="page-header mb-4">
                <h6 className="text-muted">Logistics &gt; Fleet</h6>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="fw-bold">Vehicle Directory</h3>
                        <p className="text-muted">Monitor and manage all mill transport vehicles.</p>
                    </div>
                    <Button className="add-btn px-4" onClick={() => navigate("/lorries/add")}>
                        <i className="bx bx-plus me-1"></i> Add New Lorry
                    </Button>
                </div>
            </div>

            {/* SUMMARY DASHBOARD */}
            <Row className="mb-4 g-3">
                <Col md={4}>
                    <Card className="border-0 shadow-sm bg-theme text-white rounded-4">
                        <CardBody className="p-4 d-flex align-items-center">
                            <div className="bg-white bg-opacity-25 p-3 rounded-circle me-3"><i className="bx bxs-truck fs-3"></i></div>
                            <div><h3 className="fw-bold mb-0">{summary.total}</h3><small className="opacity-75">Total Vehicles</small></div>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 border-start border-4 border-success">
                        <CardBody className="p-4 d-flex align-items-center">
                            <div className="bg-success bg-opacity-10 p-3 rounded-circle me-3 text-success"><i className="bx bx-check-double fs-3"></i></div>
                            <div><h3 className="fw-bold mb-0 text-success">{summary.active}</h3><small className="text-muted">Active</small></div>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 border-start border-4 border-danger">
                        <CardBody className="p-4 d-flex align-items-center">
                            <div className="bg-danger bg-opacity-10 p-3 rounded-circle me-3 text-danger"><i className="bx bx-power-off fs-3"></i></div>
                            <div><h3 className="fw-bold mb-0 text-danger">{summary.inactive}</h3><small className="text-muted">Inactive</small></div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* FILTERS */}
            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
                <CardBody className="p-3">
                    <Row className="g-3 align-items-end">
                        <Col md={5}>
                            <Label className="small fw-bold">Search Vehicle</Label>
                            <Input
                                placeholder="Lorry No, Driver Name or Phone..."
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                                onKeyDown={(e) => e.key === 'Enter' && loadLorries()}
                            />
                        </Col>
                        <Col md={4}>
                            <Label className="small fw-bold">Status Filter</Label>
                            <Select
                                options={statusOptions}
                                styles={customSelectStyles}
                                placeholder="Select Status..."
                                isClearable
                                value={filters.status}
                                onChange={(opt) => setFilters({ ...filters, status: opt, page: 1 })}
                            />
                        </Col>
                        <Col md={3}>
                            <Button color="primary" block className="py-2 fw-bold" onClick={loadLorries}>
                                <i className="bx bx-filter-alt me-1"></i> Apply Filters
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* DATA TABLE */}
            <Card className="border-0 shadow-sm" style={{ borderRadius: '15px' }}>
                <CardBody className="p-0">
                    <Table responsive hover className="mb-0 custom-table align-middle text-center">
                        <thead className="bg-light text-muted small text-uppercase">
                            <tr>
                                <th className="ps-4 text-start">S.No</th>
                                <th className="text-start">Vehicle Details</th>
                                <th>Driver Info</th>
                                <th>Operation Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lorries.length > 0 ? (
                                lorries.map((item, index) => (
                                    <tr key={item.id}>
                                        <td className="ps-4 text-start text-muted">{(filters.page - 1) * filters.limit + index + 1}</td>
                                        <td className="text-start">
                                            <div className="fw-bold fs-6">{item.lorry_number}</div>
                                            <small className="text-muted">Registered: {new Date(item.created_at).toLocaleDateString()}</small>
                                        </td>
                                        <td>
                                            <div className="text-capitalize fw-bold">{item.driver_name}</div>
                                            <small className="text-primary"><i className="bx bx-phone me-1"></i>{item.driver_phone}</small>
                                        </td>
                                        <td>
                                            <div className="form-check form-switch d-flex justify-content-center align-items-center">
                                                <input
                                                    className="form-check-input me-2"
                                                    type="checkbox"
                                                    checked={item.status == 1}
                                                    onChange={() => handleStatusToggle(item.id, item.status)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                <Badge color={item.status == 1 ? "success" : "danger"} pill>
                                                    {item.status == 1 ? "ACTIVE" : "INACTIVE"}
                                                </Badge>
                                            </div>
                                        </td>
                                        <td>
                                            <Button color="link" className="p-0 text-info" onClick={() => navigate(`/vehicles/${item.id}`)}>
                                                <i className="bx bx-edit-alt fs-5"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="5" className="py-5 text-muted italic">No vehicles found matching your criteria.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </CardBody>
            </Card>

            {/* PAGINATION */}
            {pagination?.total_pages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination className="pagination-rounded">
                        <PaginationItem disabled={filters.page <= 1}>
                            <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters.page - 1 })} />
                        </PaginationItem>
                        {[...Array(pagination?.total_pages)].map((_, i) => (
                            <PaginationItem active={i + 1 === filters.page} key={i}>
                                <PaginationLink onClick={() => setFilters({ ...filters, page: i + 1 })}>{i + 1}</PaginationLink>
                            </PaginationItem>
                        ))}
                        <PaginationItem disabled={filters.page >= pagination?.total_pages}>
                            <PaginationLink next onClick={() => setFilters({ ...filters, page: filters.page + 1 })} />
                        </PaginationItem>
                    </Pagination>
                </div>
            )}
        </div>
    );
};

export default LorryList;